/**
 * Generic field-policy types.
 *
 * Deliberately free of any domain vocabulary: no country, no flow, no declaration. A feature
 * supplies its own context type and its own variant map; this module only describes what a rule
 * looks like and how a field's value is obtained.
 *
 * Must stay importable from node: types and pure functions only, never `@utils`/`@hooks`/
 * `@components` (those reach `window.location` at module load).
 */

export interface FieldRule {
  present: boolean;
  disabled: boolean;
  /** Feature-defined extra lock, applied by the factory's `lock` hook. */
  amendmentLocked: boolean;
  label: string;
  code?: string;
  widget: string;
  coerce?: 'number';
  headerToggle?: string;
  dataPw?: string;
  /**
   * Which sources the field's `code` list is looked up against.
   *
   * Sits beside `code` because they are two halves of one lookup - `code` names the list, this
   * names where it comes from. Supplied per context by the policy's `systemsFor`, and overridable
   * by a base block or a variant overlay for a field that needs its own.
   *
   * Typed as strings so the framework stays free of any codelist vocabulary.
   */
  systems?: string[];
  /**
   * Which section owns the field, when more than one could render it.
   *
   * Undefined - the default - means "whichever section asks", so a field that lives in exactly one
   * place needs no section at all. Naming one lets a variant *move* a field between sections, which
   * a single `present` boolean cannot express: the same field can be authored in one tab for one
   * country/flow and another tab for the rest.
   */
  section?: string;
  /**
   * Carried through from `FieldDef.items` (see there, and `ItemFieldSpec`) so a widget reads it off
   * the resolved rule exactly like `code`/`widget` - no second lookup back into the registry. A
   * variant CAN override this (see `VariantDelta`) - always a wholesale replace of the map, never a
   * per-sub-field merge, resolved the same way any other flat rule prop is.
   */
  items?: Record<string, ItemFieldSpec<any>>;
  /**
   * Carried through from `FieldDef.createNewItem` - a blank item for THIS array's own "add" control,
   * the schema's single statement of what an empty row looks like (mirrors `items` itself, and the
   * same role `ItemFieldSpec.createNewItem` plays one level down for a nested array). Defaults to
   * `() => ({})` when unset, same as a nested array's own default.
   */
  createNewItem?: () => unknown;
}

/**
 * The slice of a schema validator this layer needs.
 *
 * Structural rather than an import: the framework depends on React and lodash only, and staying
 * library-agnostic here costs three lines. A zod schema satisfies this as-is.
 */
export interface SchemaValidator {
  safeParse: (value: unknown) => { success: boolean; error?: { issues?: { message?: string }[] } };
}

/**
 * How a field's value is checked.
 *
 * A SCHEMA when the rule is about the value alone - a zod schema satisfies `SchemaValidator` as-is.
 * A FUNCTION when the rule needs other fields: it receives the whole form values (typed by the
 * feature) first, and the field's own value second, so a cross-field rule reads siblings directly
 * instead of being handed a path string.
 */
export type FieldValidator<TValues = unknown> = SchemaValidator | ((values: TValues, value: unknown) => string | undefined);

export interface ConditionalRule<TSelector> {
  when: TSelector;
  then: Partial<FieldRule>;
}

/**
 * How a field's initial value is obtained from the entity.
 *
 * - `'path'` (default) - `get(entity, <registry key>)`
 * - `'read'`           - the key differs from the source path, or a default applies
 * - `'none'`           - display-only; never enters form state
 * - `'computed'`       - built by an explicit builder producing a subtree from several sources
 */
export type FieldSource = 'path' | 'read' | 'none' | 'computed';

/**
 * Who decides whether the field renders.
 *
 * - `'policy'` (default) - `base` + rules + variant overlays are authoritative
 * - `'jsx'`              - registered for form state only; `base` absent and `resolve` refuses it
 */
export type VisibilityOwner = 'policy' | 'jsx';

/**
 * One item's sub-field, for an array-of-objects field's `items` map (the value at each index, keyed
 * by a path relative to that one item - e.g. `identificationNumber`, not the array's own path).
 *
 * Deliberately NOT a `FieldDef`: an item's own present/disabled never varies by context - only the
 * ARRAY's does, via its own `base`/`rules`, exactly like any other field. So an item sub-field needs
 * no `base`, no `rules`, no variant overlay of its own - just enough to render and validate it.
 * `disabled` still reaches every item's widgets; it is inherited from the array's own resolved rule,
 * never re-decided per item.
 *
 * `items` recurses for an item sub-field that is itself an array of objects (e.g. a container's
 * `seals`) - the same one-level-at-a-time nesting `FieldDef.items` uses, just one level deeper.
 */
export interface ItemFieldSpec<TValues = unknown> {
  /** A function for an index-aware label (e.g. "Container 2") - most items just need a plain string. */
  label?: string | ((index: number) => string);
  widget: string;
  code?: string;
  coerce?: 'number';
  validate?: FieldValidator<TValues>;
  /**
   * A plain map for a nested array whose own shape never depends on the outer item - e.g. two
   * independent sibling arrays. A FUNCTION of the outer index when a nested leaf's own `dataPw` (or
   * anything else) must incorporate it - e.g. a container's `seals`, whose id must read
   * `container-list-0-seal-1-input`, not just `seal-1-input`: the seal-level spec closes over
   * `containerIdx` from this function's own parameter, since a nested spec has no other way to see
   * an ancestor's index (it only ever receives its OWN local index, the same as `label`/`dataPw`).
   */
  items?: Record<string, ItemFieldSpec<TValues>> | ((outerIndex: number) => Record<string, ItemFieldSpec<TValues>>);
  /** Required when `items` is set (this sub-field is itself an array) - a blank item for ITS OWN
   *  "add" control, the same role `PolicyArrayFieldBaseProps.createNewItem` plays one level up. */
  createNewItem?: () => unknown;
  /** Add-button text for this nested array (e.g. "Add Seal") - defaults to `List`'s own "Add". */
  addButtonLabel?: string;
  /**
   * Defaults to `${array's own dataPw}-${this sub-path}` - set explicitly only when that reads
   * oddly. A function for an index-aware id (e.g. an e2e fixture expecting `routing-country-0`,
   * `routing-country-1`), mirroring `label`'s own string-or-function shape.
   */
  dataPw?: string | ((index: number) => string);
}

export interface FieldDef<TSelector, TValues = unknown> {
  /**
   * `amendmentLocked` is optional and defaults to false: it is the exception, not the rule - it is
   * set by variant overlays, never by a base block. `items`/`createNewItem` are excluded too - they
   * belong on `FieldDef` itself (below), the one canonical place to set them, not duplicated here.
   */
  base?: Omit<FieldRule, 'disabled' | 'amendmentLocked' | 'items' | 'createNewItem'> & { amendmentLocked?: boolean };
  rules?: ConditionalRule<TSelector>[];
  source?: FieldSource;
  read?: (entity: unknown) => unknown;
  visibility?: VisibilityOwner;
  /** Form-only key, stripped before submitting. Orthogonal to `source`. */
  viewModel?: true;
  /**
   * Optional schema for this field's value.
   *
   * Consulted ONLY when the resolved rule says the field is present: a field a variant does not
   * render must never be able to block that variant's submit. That conditionality is the whole
   * reason validation belongs beside the visibility rules rather than in a component.
   */
  validate?: FieldValidator<TValues>;
  /**
   * Marks this field's value as an array, each item described by this map of item-relative
   * sub-paths instead of the field being one scalar leaf. The array's OWN `present`/`disabled` still
   * comes from `base`/`rules` exactly like any other field - this only describes what is INSIDE each
   * item. See `ItemFieldSpec` for why an item's own visibility is never expressed here.
   *
   * Declare the WIDEST possible shape here - every sub-field any variant could ever show - since
   * `FormValuesOf` only ever reads this base declaration, never a variant's. A variant that shows
   * FEWER sub-fields for its cell overrides `items` with a narrower map (see `VariantDelta`); one
   * that needs MORE than this declares was never told about it and cannot add it.
   */
  items?: Record<string, ItemFieldSpec<TValues>>;
  /** A blank item for THIS array's own "add" control - see `FieldRule.createNewItem`. */
  createNewItem?: () => unknown;
}

/**
 * Registers a field for form state only - seeded (and, if `viewModel`, stripped) like any other
 * field, but with no `base`, so `resolve` refuses it and a component can never ask the policy for
 * its rule. Use this for a field whose presence is a hand-computed JSX concern (an async lookup, a
 * side effect, a builder-produced subtree) that still needs to come from the registry so the
 * form's whole initial state has one source, rather than half from here and half hand-assembled in
 * the component.
 */
export const formOnly = <TSelector, TValues = unknown>(extra: Partial<FieldDef<TSelector, TValues>> = {}): FieldDef<TSelector, TValues> => ({
  visibility: 'jsx',
  ...extra,
});

/**
 * A variant contributes deltas for its own cell - nothing else.
 *
 * Flat props and `rules` combine: flat first, then matching rules. Treating them as alternatives
 * silently drops the flat half, which is a bug worth never reintroducing.
 *
 * `validate` is separate from the flat `FieldRule` props: it is consulted by `buildErrors`, never
 * merged into a resolved `FieldRule` (which has no slot for it - see `applyOverlays`, which strips
 * it back out before spreading). Lets a cross-field rule that only makes sense for one variant cell
 * (e.g. one country) live beside that cell's other deltas instead of on every cell via `base`.
 *
 * `items` CAN be set here, unlike `FieldDef.base` (which excludes it to keep one canonical
 * declaration site): a variant that sets it replaces the base's `items` map WHOLESALE, the same
 * plain-object-spread semantics every other flat prop already gets - never a deep merge. This is how
 * a sub-field that only exists for one variant (e.g. a container's `seals`, export-only) is
 * expressed: the base declares the WIDEST shape (so `FormValuesOf`, which only ever sees the base
 * registry, types every possible sub-field), and a variant that should show fewer of them overrides
 * `items` with a narrower map - never the other way around, since a variant has no way to add a
 * sub-field `FormValuesOf` was never told about.
 */
export type VariantDelta<TSelector, TValues = unknown> = Partial<FieldRule> & {
  rules?: ConditionalRule<TSelector>[];
  validate?: FieldValidator<TValues>;
};

export type Variant<TSelector, TPath extends string = string, TValues = unknown> = Partial<Record<TPath, VariantDelta<TSelector, TValues>>>;

/**
 * The object shape a registry's dotted paths imply.
 *
 * Lets a form be typed from `typeof FIELDS` alone, with no hand-written value interface to keep in
 * step. Leaves are `unknown`: the registry records where a value lives, not what it is - a field's
 * own type still comes from the entity model at the point of use. The one exception is a field with
 * `items` (see `FieldDef.items`/`ItemFieldSpec`): its leaf is `Array<{...}>` instead of `unknown`,
 * shaped from its own item spec, since the registry DOES know that much about it.
 */
type Nest<P extends string, Leaf> = P extends `${infer Head}.${infer Rest}` ? { [K in Head]: Nest<Rest, Leaf> } : { [K in P]: Leaf };

/** The item-spec map behind an `items` value, whether declared directly or behind a per-outer-index function. */
type ItemsShapeOf<TRaw> = TRaw extends (...args: any[]) => infer TReturn
  ? TReturn extends Record<string, ItemFieldSpec<any>>
    ? TReturn
    : never
  : TRaw extends Record<string, ItemFieldSpec<any>>
    ? TRaw
    : never;

/** An item spec's own value shape - recurses once more per nested `items` (e.g. a container's `seals`). */
type ItemValuesOf<TItems extends Record<string, ItemFieldSpec<any>>> = {
  [K in keyof TItems]: TItems[K] extends { items: infer TRaw }
    ? [ItemsShapeOf<TRaw>] extends [never]
      ? unknown
      : ItemValuesOf<ItemsShapeOf<TRaw>>[]
    : unknown;
};

/** `unknown` for a plain leaf field; `Array<{...}>` for a field with `items`. */
type LeafOf<TDef> = TDef extends { items: infer TItems extends Record<string, ItemFieldSpec<any>> } ? ItemValuesOf<TItems>[] : unknown;

type UnionToIntersection<U> = (U extends unknown ? (x: U) => void : never) extends (x: infer I) => void ? I : never;

/** Collapses the intersection of per-path shapes so the result reads as one object type. */
type Flatten<T> = T extends object ? { [K in keyof T]: Flatten<T[K]> } : T;

export type FormValuesOf<TFields extends object> = Flatten<
  UnionToIntersection<{ [K in keyof TFields & string]: Nest<K, LeafOf<TFields[K]>> }[keyof TFields & string]>
>;
